import React, { useEffect, useMemo, useState } from "react";
import { Col, Container, Row } from "reactstrap";
import classNames from "classnames";
import { toast } from "react-toastify";
import {
    getAuditingTemplateList,
    submitAuditingTemplateData,
} from "model/AuditingTemplateModel";
import { allowOnlyNumber } from "helpers/GeneralHelper";

const REQUIRED_TOTAL_WEIGHT = 102;

const AuditingTemplates = () => {
    const [templateSections, setTemplateSections] = useState([]);
    const [templateMeta, setTemplateMeta] = useState({
        templateId: "",
        templateName: "",
    });
    const [isLoading, setIsLoading] = useState(false);

    const groupDetailsBySection = (detailList = []) => {
        const sectionMap = detailList.reduce((result, detailItem) => {
            const sectionTitle = detailItem?.legendTitle || "Others";
            if (!result[sectionTitle]) result[sectionTitle] = [];

            result[sectionTitle].push({
                auditingParameterId: detailItem?.auditingParameterId || "",
                name: detailItem?.parameterTitle || "",
                use: detailItem?.isUse,
                must: detailItem?.isDisqualified,
                weight:
                    detailItem?.weightagePercentage === null ||
                        detailItem?.weightagePercentage === undefined
                        ? ""
                        : Number(detailItem?.weightagePercentage),
            });

            return result;
        }, {});

        // it use for convert object into array
        return Object.entries(sectionMap).map(([section, items]) => ({ section, items }));
    };

    const calculateTotalWeight = (sections = []) => {
        return sections.reduce((sectionTotal, sectionItem) => {
            const itemWeightSum = sectionItem?.items?.reduce((itemTotal, rowItem) => {
                if (rowItem.must || !rowItem.use) return itemTotal;
                return itemTotal + Number(rowItem.weight || 0);
            }, 0);

            return sectionTotal + itemWeightSum;
        }, 0);
    };

    const loadTemplateData = async () => {
        setIsLoading(true);
        const response = await getAuditingTemplateList({});
        const responseData = response?.data;
        const detailList = responseData?.details || [];

        if (detailList && detailList.length) {
            setTemplateSections(groupDetailsBySection(detailList));
            setTemplateMeta({
                templateId: responseData?.templateId || "",
                templateName: responseData?.templateName || "",
            });
        }
        setIsLoading(false);
    };

    useEffect(() => {
        loadTemplateData();
    }, []);

    const updateCheckboxValue = (sectionIndex, itemIndex, fieldName) => {
        setTemplateSections((prevSections) =>
            prevSections.map((section, currentSectionIndex) => {
                if (currentSectionIndex !== sectionIndex) return section;

                return {
                    ...section,
                    items: section.items.map((item, currentItemIndex) => {
                        if (currentItemIndex !== itemIndex) return item;

                        const updatedValue = !item[fieldName];
                        return { ...item, [fieldName]: updatedValue };
                    }),
                };
            })
        );
    };

    const updateWeightValue = (sectionIndex, itemIndex, inputValue) => {
        const numericValue = allowOnlyNumber(inputValue);
        setTemplateSections((prevSections) =>
            prevSections.map((section, currentSectionIndex) => {
                if (currentSectionIndex !== sectionIndex) return section;

                return {
                    ...section,
                    items: section.items.map((item, currentItemIndex) => {
                        if (currentItemIndex !== itemIndex) return item;
                        return {
                            ...item,
                            weight: numericValue === "" ? "" : Number(numericValue),
                        };
                    }),
                };
            })
        );
    };

    const totalWeight = useMemo(
        () => calculateTotalWeight(templateSections),
        [templateSections]
    );

    const buildSavePayload = () => {
        const detailPayload = templateSections.flatMap((sectionItem) =>
            sectionItem.items.map((rowItem) => ({
                auditingParameterId: rowItem?.auditingParameterId || "",
                parameterTitle: rowItem?.name || "",
                legendTitle: sectionItem?.section || "",
                isUse: rowItem?.use,
                isDisqualified: rowItem?.must,
                weightagePercentage: rowItem?.must || !rowItem?.use ? 0 : Number(rowItem?.weight || 0),
            }))
        );

        return {
            templateId: templateMeta?.templateId || "",
            templateName: templateMeta?.templateName || "",
            details: detailPayload,
        };
    };

    const handleTemplateNameChange = (event) => {
        const { value } = event.target;
        setTemplateMeta((prev) => ({
            ...prev,
            templateName: value,
        }));
    };

    const handleSave = async () => {
        if (totalWeight !== REQUIRED_TOTAL_WEIGHT) {
            toast.error(
                `Total weight must be exactly 102%. Current total: ${Number(
                    totalWeight
                ).toFixed(2)}%`
            );
            return;
        }

        setIsLoading(true);
        const payload = buildSavePayload();
        const response = await submitAuditingTemplateData(payload);

        if (response?.success || response?.status === "success") {
            await loadTemplateData();
            toast.success(response?.message || "Template saved successfully");
        } else {
            toast.error(response?.message || "Unable to save template");
            setIsLoading(false);
        }
    };

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <Row>
                        <Col md={6}>
                            <div className="mb-3">
                                <label className="form-label fw-semibold">Template Name</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Enter template name"
                                    value={templateMeta.templateName}
                                    onChange={handleTemplateNameChange}
                                />
                            </div>
                        </Col>
                    </Row>

                    {/* NOTE BANNER */}
                    <div className="note-banner">
                        <strong>Note:</strong> If you enable <i>Must Be Qualified</i> for a parameter,
                        its weightage is ignored and does not affect scoring.
                    </div>

                    <div className="d-flex justify-content-end mb-2 font-size-14">
                        <strong>
                            Total Weight: <span className={`${Number(totalWeight) === 102 ? '' : 'text-danger'}`}>{Number(totalWeight)?.toFixed(2)}%</span>
                        </strong>
                    </div>

                    {/* TABLE */}
                    <div className="table-responsive">
                        <table className="template-table">
                            <thead>
                                <tr>
                                    <th className="text-center font-size-15">Parameter</th>
                                    <th className="text-center font-size-15">Use?</th>
                                    <th className="text-center font-size-15">Must Be Qualified</th>
                                    <th className="text-center font-size-15">Weight (%)</th>
                                </tr>
                            </thead>
                            <tbody>
                                {templateSections.map((section, sIndex) => (
                                    <React.Fragment key={sIndex}>
                                        <tr className="section-row">
                                            <td colSpan="4">{section.section}</td>
                                        </tr>

                                        {section.items.map((item, iIndex) => (
                                            <tr key={iIndex}>
                                                <td>{item.name}</td>

                                                <td className="text-center">
                                                    <input
                                                        type="checkbox"
                                                        checked={item.use}
                                                        onChange={() =>
                                                            updateCheckboxValue(sIndex, iIndex, "use")
                                                        }
                                                        className="form-check-input"
                                                    />
                                                </td>

                                                <td className="text-center">
                                                    <input
                                                        type="checkbox"
                                                        checked={item.must}
                                                        onChange={() =>
                                                            updateCheckboxValue(sIndex, iIndex, "must")
                                                        }
                                                        className="form-check-input"
                                                    />
                                                </td>

                                                <td>
                                                    <input
                                                        type="number"
                                                        className={classNames("form-control", {
                                                            "bg-light": item.must || !item.use,
                                                        })}
                                                        value={item.weight}
                                                        disabled={item.must || !item.use}
                                                        onChange={(e) =>
                                                            updateWeightValue(
                                                                sIndex,
                                                                iIndex,
                                                                e.target.value
                                                            )
                                                        }
                                                    />
                                                </td>
                                            </tr>
                                        ))}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* SAVE BUTTON */}
                    <div className="text-end mb-2">
                        <button
                            className="btn btn-primary px-4 mt-2"
                            onClick={handleSave}
                            disabled={isLoading}
                        >
                            {isLoading ? "Saving..." : "Update Template"}
                        </button>
                    </div>

                </Container>
            </div>
        </React.Fragment>
    );
};

export default AuditingTemplates;